import { FC, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Box, CircularProgress, Tab, Typography } from '@mui/material'
import ContentContainer from '../../areas/layout/content-container'
import { useAuth } from '../../context/AuthContext'
import { useMediaState } from '../../context/media-state-context'
import { MediaStateItem } from '../../context/media-state.types'
import LargeCard from '../../components/cards/large-card'
import EmptyState from '../../utils/empty-state'
import { CenterBox } from '../../utils/center-box'
import { parseUtcDate } from '../../utils/parse-utc-date'
import { useMobileBreakpoints } from '../../utils/mobile-breakpoints'
import { UserNotFound } from '../Status/status-page'
import usersApi, {
  PublicProfile,
  PublicProfileComment,
} from '../../client/users-api'
import {
  PageWrapper,
  HeaderWrapper,
  AvatarWrapper,
  StyledAvatar,
  ItemsBox,
  StyledTabsForProfile,
  SectionTitle,
} from '../Profile/profile-styles'
import {
  ProfileHeaderLayout,
  ProfileIdentity,
  AvatarImage,
  StatsGrid,
  StatChip,
  StatValue,
  StatLabel,
  ShowAllItemsButton,
  BioText,
  BadgesRow,
  BadgeChip,
  GenreChipsRow,
  GenreChip,
  SectionBlock,
  SectionHeadingRow,
  MatchChip,
  HorizontalStrip,
  PrivateNoticeBox,
  CommentsListBox,
  CommentCard,
  CommentHeaderRow,
  CommentMediaLink,
  CommentDateText,
  CommentContentText,
} from './user-profile-styles'

type TabKey = 'watched' | 'favorites' | 'watchlist'

// Keeps the page short enough that comments stay reachable without scrolling
// past a whole library; "Show all" expands the active tab.
const VISIBLE_ITEMS_CAP = 5

const statDefs = [
  { label: 'Watched Episodes', key: 'totalEpisodesWatched' },
  { label: 'Seasons', key: 'totalSeasonsWatched' },
  { label: 'Favorites', key: 'totalFavorites' },
  { label: 'Watchlist', key: 'totalWatchlist' },
  { label: 'Movies', key: 'totalMoviesWatched' },
  { label: 'Shows', key: 'totalShowsStarted' },
] as const

const formatCommentDate = (iso: string) =>
  parseUtcDate(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })

// Episode comments live on season pages; everything else on the detail page.
const commentMediaPath = (comment: PublicProfileComment) => {
  const type = comment.mediaType.toLowerCase()
  if (type === 'movie') return `/details/movie/${comment.tmdbMediaId}`
  if (type === 'episode' && comment.seasonNumber != null) {
    return `/shows/${comment.tmdbMediaId}/season/${comment.seasonNumber}`
  }
  return `/details/show/${comment.tmdbMediaId}`
}

const commentMediaLabel = (comment: PublicProfileComment) => {
  const title = comment.mediaTitle || 'View title'
  if (comment.mediaType.toLowerCase() === 'episode' && comment.seasonNumber != null) {
    return `${title} · Season ${comment.seasonNumber}`
  }
  return title
}

const mediaKey = (item: MediaStateItem) =>
  `${item.mediaType?.toLowerCase()}-${item.tmdbId}`

// Most frequent genres across someone's history + favorites.
const topGenresOf = (collections: MediaStateItem[][], count: number) => {
  const genreCounts = new Map<string, number>()
  collections.flat().forEach((item) => {
    ;(item.genres ?? []).forEach((genre) => {
      genreCounts.set(genre, (genreCounts.get(genre) ?? 0) + 1)
    })
  })
  return Array.from(genreCounts.entries())
    .sort((first, second) => second[1] - first[1])
    .slice(0, count)
    .map(([genre]) => genre)
}

const UserProfilePage: FC = () => {
  const { id } = useParams<{ id: string }>()
  const { isMobileLG } = useMobileBreakpoints()
  const { user: viewer } = useAuth()
  const { mediaState: viewerMediaState } = useMediaState()

  const [profile, setProfile] = useState<PublicProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [tab, setTab] = useState<TabKey>('watched')
  const [showAllItems, setShowAllItems] = useState(false)

  useEffect(() => {
    // Numeric ids (legacy links) and username handles both resolve server-side.
    const idOrUsername = (id ?? '').trim()
    if (!idOrUsername) {
      setNotFound(true)
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setNotFound(false)
    setProfile(null)
    setTab('watched')

    const fetchProfile = async () => {
      try {
        const response = await usersApi.getPublicProfile(idOrUsername)
        // The API serves the SPA's index.html for unknown routes (200 with an
        // HTML string), so anything that isn't a profile object counts as
        // not found - e.g. an API deployment that predates this endpoint.
        const data = response.data
        if (!data || typeof data !== 'object' || typeof data.id !== 'number') {
          if (!cancelled) setNotFound(true)
          return
        }
        if (!cancelled) setProfile(data)
      } catch (error) {
        if (!cancelled) setNotFound(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchProfile()
    return () => {
      cancelled = true
    }
  }, [id])

  const items = useMemo(() => {
    if (!profile || profile.isPrivate) return []
    if (tab === 'watched') {
      return [...(profile.history ?? [])].sort(
        (first, second) =>
          new Date(second.lastWatchedOnUtc ?? 0).getTime() -
          new Date(first.lastWatchedOnUtc ?? 0).getTime(),
      )
    }
    return (tab === 'favorites' ? profile.favorites : profile.watchlist) ?? []
  }, [profile, tab])

  const topGenres = useMemo(() => {
    if (!profile || profile.isPrivate) return []
    return topGenresOf([profile.history ?? [], profile.favorites ?? []], 5)
  }, [profile])

  // Shared watched titles + a taste match score, only when a signed-in viewer
  // looks at someone ELSE's public profile. 60% weight on shared titles,
  // 40% on how much the top-genre sets agree.
  const tasteOverlap = useMemo(() => {
    if (!profile || profile.isPrivate || !viewer || !viewerMediaState) return null
    if (viewer.id === profile.id) return null

    const viewerWatchedKeys = new Set(
      (viewerMediaState.history ?? []).map((item) => mediaKey(item)),
    )
    const sharedWatched = (profile.history ?? []).filter((item) =>
      viewerWatchedKeys.has(mediaKey(item)),
    )

    const smallerLibrary = Math.max(
      1,
      Math.min(viewerWatchedKeys.size, (profile.history ?? []).length),
    )
    const titleRatio = sharedWatched.length / smallerLibrary

    const viewerGenres = topGenresOf(
      [viewerMediaState.history ?? [], viewerMediaState.favorites ?? []],
      5,
    )
    const profileGenres = topGenresOf(
      [profile.history ?? [], profile.favorites ?? []],
      5,
    )
    const genreUnion = new Set([...viewerGenres, ...profileGenres])
    const genreIntersection = viewerGenres.filter((genre) =>
      profileGenres.includes(genre),
    )
    const genreRatio =
      genreUnion.size === 0 ? 0 : genreIntersection.length / genreUnion.size

    const matchPercent = Math.min(
      100,
      Math.round(titleRatio * 60 + genreRatio * 40),
    )

    return { sharedWatched, matchPercent }
  }, [profile, viewer, viewerMediaState])

  if (loading) {
    return (
      <ContentContainer>
        <CenterBox>
          <CircularProgress size={48} />
        </CenterBox>
      </ContentContainer>
    )
  }

  if (notFound || !profile) {
    return <UserNotFound />
  }

  return (
    <ContentContainer>
      <PageWrapper>
        <HeaderWrapper>
          <ProfileHeaderLayout isMobile={isMobileLG}>
            <ProfileIdentity isMobile={isMobileLG}>
              <AvatarWrapper>
                <StyledAvatar>
                  {profile.profilePictureUrl ? (
                    <AvatarImage
                      src={profile.profilePictureUrl}
                      alt={profile.name}
                    />
                  ) : (
                    profile.name?.charAt(0)
                  )}
                </StyledAvatar>
              </AvatarWrapper>

              <Box>
                <Typography fontSize={20} fontWeight={600}>
                  {profile.name}
                </Typography>
                {profile.username && (
                  <Typography fontSize={13} color='text.secondary'>
                    @{profile.username}
                  </Typography>
                )}
                <Typography
                  fontSize={12}
                  fontStyle='italic'
                  color='text.secondary'
                >
                  Movieceptionite since{' '}
                  {parseUtcDate(profile.memberSinceUtc).getFullYear()}
                </Typography>
                {!profile.isPrivate && profile.bio && (
                  <BioText>{profile.bio}</BioText>
                )}
              </Box>
            </ProfileIdentity>

            {!profile.isPrivate && (
              <StatsGrid isMobile={isMobileLG}>
                {statDefs.map((stat) => (
                  <StatChip key={stat.key}>
                    <StatValue>{profile.stats?.[stat.key] ?? 0}</StatValue>
                    <StatLabel>{stat.label}</StatLabel>
                  </StatChip>
                ))}
              </StatsGrid>
            )}
          </ProfileHeaderLayout>
        </HeaderWrapper>

        {profile.isPrivate ? (
          <PrivateNoticeBox>
            <Typography fontSize={16} fontWeight={600}>
              This profile is private
            </Typography>
            <Typography fontSize={13}>
              {profile.name} keeps their activity to themselves.
            </Typography>
          </PrivateNoticeBox>
        ) : (
          <>
            {profile.badges.length > 0 && (
              <BadgesRow>
                {profile.badges.map((badge) => (
                  <BadgeChip key={badge.key} title={badge.description}>
                    {badge.label}
                  </BadgeChip>
                ))}
              </BadgesRow>
            )}

            {profile.pinnedShowcase.length > 0 && (
              <SectionBlock>
                <SectionTitle>Showcase</SectionTitle>
                <HorizontalStrip>
                  {profile.pinnedShowcase.map((pinnedItem) => (
                    <LargeCard
                      key={`pinned-${pinnedItem.tmdbId}`}
                      id={pinnedItem.tmdbId}
                      tmdbId={pinnedItem.tmdbId}
                      type={
                        pinnedItem.mediaType?.toLowerCase() === 'show'
                          ? 'show'
                          : 'movie'
                      }
                      title={pinnedItem.title}
                      year={
                        pinnedItem.releaseYear
                          ? String(pinnedItem.releaseYear)
                          : undefined
                      }
                      posterUrl={pinnedItem.posterPath ?? undefined}
                    />
                  ))}
                </HorizontalStrip>
              </SectionBlock>
            )}

            {topGenres.length > 0 && (
              <GenreChipsRow>
                {topGenres.map((genre) => (
                  <GenreChip key={genre}>{genre}</GenreChip>
                ))}
              </GenreChipsRow>
            )}

            {tasteOverlap && tasteOverlap.sharedWatched.length > 0 && (
              <SectionBlock>
                <SectionHeadingRow>
                  <SectionTitle>You both watched</SectionTitle>
                  <MatchChip>{tasteOverlap.matchPercent}% match</MatchChip>
                </SectionHeadingRow>
                <HorizontalStrip>
                  {tasteOverlap.sharedWatched.slice(0, 12).map((item) => (
                    <LargeCard
                      key={`shared-${item.tmdbId}`}
                      id={item.tmdbId}
                      tmdbId={item.tmdbId}
                      type={
                        item.mediaType?.toLowerCase() === 'show'
                          ? 'show'
                          : 'movie'
                      }
                      title={item.title}
                      year={
                        item.releaseYear ? String(item.releaseYear) : undefined
                      }
                      posterUrl={item.posterPath ?? undefined}
                      genres={item.genres ?? []}
                    />
                  ))}
                </HorizontalStrip>
              </SectionBlock>
            )}

            <StyledTabsForProfile
              value={tab}
              onChange={(_, value) => {
                setTab(value)
                setShowAllItems(false)
              }}
            >
              <Tab value='watched' label='History' />
              <Tab value='favorites' label='Favorites' />
              <Tab value='watchlist' label='Watchlist' />
            </StyledTabsForProfile>

            {items.length === 0 ? (
              <EmptyState title='Nothing here yet' />
            ) : (
              <ItemsBox>
                {(showAllItems ? items : items.slice(0, VISIBLE_ITEMS_CAP)).map((item) => (
                  <LargeCard
                    key={`${tab}-${item.tmdbId}`}
                    id={item.tmdbId}
                    tmdbId={item.tmdbId}
                    type={
                      item.mediaType?.toLowerCase() === 'show' ? 'show' : 'movie'
                    }
                    title={item.title}
                    year={item.releaseYear ? String(item.releaseYear) : undefined}
                    posterUrl={item.posterPath ?? undefined}
                    genres={item.genres ?? []}
                    lastWatchedOnUtc={item.lastWatchedOnUtc}
                    episodesWatchedCount={item.episodesWatchedCount}
                    episodesTotal={
                      item.numberOfEpisodes ?? item.episodesWatchedCount
                    }
                  />
                ))}
              </ItemsBox>
            )}

            {!showAllItems && items.length > VISIBLE_ITEMS_CAP && (
              <ShowAllItemsButton onClick={() => setShowAllItems(true)}>
                Show all {items.length}
              </ShowAllItemsButton>
            )}

            {(profile.recentComments ?? []).length > 0 && (
              <CommentsListBox>
                <SectionTitle>Recent Comments</SectionTitle>
                {profile.recentComments.map((comment) => (
                  <CommentCard key={comment.id}>
                    <CommentHeaderRow>
                      <CommentMediaLink to={commentMediaPath(comment)}>
                        {commentMediaLabel(comment)}
                      </CommentMediaLink>
                      <CommentDateText>
                        {formatCommentDate(comment.createdOnUtc)}
                      </CommentDateText>
                    </CommentHeaderRow>
                    <CommentContentText>{comment.content}</CommentContentText>
                  </CommentCard>
                ))}
              </CommentsListBox>
            )}
          </>
        )}
      </PageWrapper>
    </ContentContainer>
  )
}

export default UserProfilePage
